import { RAW_MATERIAL_KIND_LABELS } from "@artisancode/api-types";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

import {
  CountedQtyField,
  OpnameDiffBadge,
  ReasonSelect,
  RemoveItemButton,
  RepackingCountField,
  type OpnameItemProps,
} from "./stock-opname-item-fields";
import { getOpnameItemState, opnameBuckets } from "./stock-opname-utils";

/** Mobile layout of one counted line. */
export function StockOpnameItemCard(props: OpnameItemProps) {
  const {
    item,
    reasons,
    isRawMaterial,
    isLocked,
    onOnHandChange,
    onReasonChange,
    onNoteChange,
    onRemove,
    error,
  } = props;
  const {
    hasDiscrepancy,
    applicableReasons,
    selectedReasonId,
    isMissingReason,
  } = getOpnameItemState(item, reasons, isRawMaterial);

  const { onHand: hasOnHand, repacking: hasRepacking } = opnameBuckets(
    isRawMaterial,
    item.kind,
  );

  return (
    <Card
      className={cn(
        "gap-2 py-3 shadow-xs",
        hasDiscrepancy && "border-amber-500/40 bg-amber-500/5",
        error && "border-destructive/60",
      )}
    >
      <CardContent className="space-y-3 px-3.5 text-xs">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="text-sm font-semibold text-foreground">
              {item.itemName}
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
              {item.itemSku && (
                <span className="font-mono">{item.itemSku}</span>
              )}
              {item.kind && (
                <Badge variant="outline" className="px-1 py-0 text-[10px]">
                  {RAW_MATERIAL_KIND_LABELS[item.kind]}
                </Badge>
              )}
              {item.categoryName && <span>• {item.categoryName}</span>}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <Badge variant="outline" className="font-mono text-[10px]">
              {item.uomName}
            </Badge>
            {!isLocked && onRemove && (
              <RemoveItemButton
                itemName={item.itemName}
                onClick={() => onRemove(item.id)}
              />
            )}
          </div>
        </div>

        {hasOnHand && (
          <CountBox
            title={isRawMaterial ? "Stok Utuh (Gudang/Segel)" : "Stok Fisik"}
            countLabel="Fisik Dihitung:"
            systemQty={item.systemOnHand}
            diff={item.diffOnHand}
            error={error?.onHand}
          >
            <CountedQtyField
              value={item.countedOnHand}
              isLocked={isLocked}
              invalid={!!error?.onHand}
              onChange={(v) => onOnHandChange(item.id, v)}
              className="h-10 w-28 font-semibold"
            />
          </CountBox>
        )}

        {hasRepacking && (
          <CountBox
            title="Stok Terbuka (Bar / Repacking)"
            countLabel="Fisik Bar:"
            systemQty={item.systemRepacking}
            diff={item.diffRepacking}
            error={error?.repacking}
          >
            <RepackingCountField
              {...props}
              inputClassName="h-10 w-24 font-semibold"
            />
          </CountBox>
        )}

        {hasDiscrepancy && (
          <div className="space-y-1.5 border-t border-border/40 pt-1">
            <Label className="text-[11px] font-medium text-muted-foreground">
              Alasan & Catatan Selisih:
            </Label>
            {isLocked ? (
              <div className="text-xs font-medium">
                {item.reasonName && (
                  <Badge variant="secondary" className="mr-1.5 text-[10px]">
                    {item.reasonName}
                  </Badge>
                )}
                <span className="text-muted-foreground">
                  {item.note || "-"}
                </span>
              </div>
            ) : (
              <div className="space-y-1.5">
                <ReasonSelect
                  reasons={applicableReasons}
                  value={selectedReasonId}
                  invalid={isMissingReason || !!error?.reason}
                  onChange={(id) => onReasonChange(item.id, id)}
                  className="min-h-[40px] w-full"
                />
                {error?.reason && <FieldError message={error.reason} />}
                <Input
                  value={item.note || ""}
                  onChange={(e) => onNoteChange(item.id, e.target.value)}
                  placeholder="Catatan rincian selisih..."
                  className="min-h-[40px] bg-background text-xs"
                />
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CountBox({
  title,
  countLabel,
  systemQty,
  diff,
  error,
  children,
}: {
  title: string;
  countLabel: string;
  systemQty: number;
  diff: number;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5 rounded-lg border bg-background/80 p-2.5">
      <div className="flex items-center justify-between text-xs">
        <span className="font-medium text-foreground">{title}</span>
        <span className="text-[11px] text-muted-foreground">
          Sistem:{" "}
          <strong className="font-mono text-foreground">
            {systemQty.toLocaleString()}
          </strong>
        </span>
      </div>
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs text-muted-foreground">{countLabel}</div>
        <div className="flex items-center gap-1.5">
          {children}
          <div className="w-14 shrink-0 text-center">
            <OpnameDiffBadge diff={diff} className="py-0.5" />
          </div>
        </div>
      </div>
      {error && <FieldError message={error} />}
    </div>
  );
}

function FieldError({ message }: { message: string }) {
  return (
    <p role="alert" className="text-[11px] font-medium text-destructive">
      {message}
    </p>
  );
}
